import {
  ArrowPathIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  LinkIcon,
  XCircleIcon,
} from '@heroicons/react/24/solid';
import React from 'react';

import { generateEntryKey } from '../../../../utils/nameMatching';
import Badge from '../../../common/Badge';
import type { MatchedPair } from '../ImportDetail.module';

interface NameMatchStepProps {
  pairs: MatchedPair[];
  pendingCount: number;
  expandedPairKey: string | null;
  onToggleExpand: (key: string | null) => void;
  onChangeStatus: (
    fileKey: string,
    rnicKey: string,
    status: 'validated' | 'rejected' | 'pending',
  ) => void;
  fileAddressMap: Map<string, string>;
  rnicAddressMap: Map<string, string>;
}

const NameMatchStep: React.FC<NameMatchStepProps> = ({
  pairs,
  pendingCount,
  expandedPairKey,
  onToggleExpand,
  onChangeStatus,
  fileAddressMap,
  rnicAddressMap,
}) => {
  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-lg font-semibold text-blue-800">
          <LinkIcon className="h-5 w-5" />
          Correspondances suggérées par nom ({pairs.length})
        </h3>
        {pendingCount > 0 && (
          <span className="text-sm text-blue-600">
            {pendingCount} en attente de validation
          </span>
        )}
      </div>
      <p className="mb-4 text-sm text-blue-700">
        Ces entrées du fichier pourraient correspondre à des entrées RNIC avec un numéro d'immatriculation différent.
        Validez ou rejetez chaque correspondance.
      </p>

      {pairs.length === 0 ? (
        <div className="rounded-lg border border-dashed border-blue-200 bg-white px-4 py-6 text-center text-sm text-blue-800">
          Aucune correspondance par nom détectée. Vous pouvez passer à l'étape suivante.
        </div>
      ) : (
      <div className="space-y-3">
        {pairs.map((pair) => {
          const fileKey = generateEntryKey(pair.fileEntry.immatriculation, pair.fileEntry.name);
          const rnicKey = generateEntryKey(pair.rnicEntry.immatriculation, pair.rnicEntry.name);
          const pairKey = `${fileKey}::${rnicKey}`;
          const isExpanded = expandedPairKey === pairKey;
          const fileAddress = fileAddressMap.get(fileKey) || '';
          const rnicAddress = rnicAddressMap.get(rnicKey) || '';
          const hasAddresses = fileAddress || rnicAddress;

          return (
            <div
              key={pairKey}
              className={`rounded-lg border bg-white p-4 ${
                pair.status === 'validated' ? 'border-green-300' : 'border-gray-200'
              }`}
            >
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="rounded bg-red-50 p-3">
                  <p className="mb-1 text-xs font-medium text-red-600">Fichier importé (pas dans RNIC)</p>
                  <p className="font-semibold text-gray-800">{pair.fileEntry.name}</p>
                  <p className="font-mono text-sm text-gray-500">{pair.fileEntry.immatriculation}</p>
                </div>
                <div className="rounded bg-yellow-50 p-3">
                  <p className="mb-1 text-xs font-medium text-yellow-600">RNIC correspondance probable à valider (pas dans fichier)</p>
                  <p className="font-semibold text-gray-800">{pair.rnicEntry.name}</p>
                  <p className="font-mono text-sm text-gray-500">{pair.rnicEntry.immatriculation}</p>
                </div>
              </div>

              {isExpanded && hasAddresses && (
                <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <p className="mb-2 text-xs font-semibold text-gray-700">Comparaison des adresses</p>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="rounded border border-red-100 bg-red-50 p-2">
                      <p className="mb-1 text-[10px] font-medium text-red-600">Adresse Fichier</p>
                      <p className="text-sm text-gray-800">{fileAddress || <span className="text-gray-400 italic">Non disponible</span>}</p>
                    </div>
                    <div className="rounded border border-yellow-100 bg-yellow-50 p-2">
                      <p className="mb-1 text-[10px] font-medium text-yellow-600">Adresse RNIC</p>
                      <p className="text-sm text-gray-800">{rnicAddress || <span className="text-gray-400 italic">Non disponible</span>}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Badge
                    mode={pair.confidenceScore && pair.confidenceScore >= 70 ? 'success' : 'warning'}
                    text={`Confiance : ${pair.confidenceScore || 0}%`}
                  />
                  {pair.status === 'validated' && <Badge mode="success" text="Validé" />}
                  {pair.status === 'pending' && <Badge mode="info" text="En attente" />}

                  {hasAddresses && (
                    <button
                      onClick={() => onToggleExpand(isExpanded ? null : pairKey)}
                      className="flex items-center gap-1 rounded bg-blue-100 px-2 py-1 text-xs font-medium text-blue-700 transition-colors hover:bg-blue-200"
                      title={isExpanded ? 'Masquer les adresses' : 'Voir les adresses'}
                    >
                      {isExpanded ? (
                        <>
                          <ChevronUpIcon className="h-3 w-3" />
                          Masquer adresses
                        </>
                      ) : (
                        <>
                          <ChevronDownIcon className="h-3 w-3" />
                          Voir adresses
                        </>
                      )}
                    </button>
                  )}
                </div>

                {pair.status === 'pending' && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => onChangeStatus(fileKey, rnicKey, 'validated')}
                      className="flex items-center gap-1 rounded bg-green-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-green-700"
                    >
                      <CheckCircleIcon className="h-4 w-4" />
                      Valider
                    </button>
                    <button
                      onClick={() => onChangeStatus(fileKey, rnicKey, 'rejected')}
                      className="flex items-center gap-1 rounded bg-red-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-red-700"
                    >
                      <XCircleIcon className="h-4 w-4" />
                      Rejeter
                    </button>
                  </div>
                )}

                {pair.status === 'validated' && (
                  <button
                    onClick={() => onChangeStatus(fileKey, rnicKey, 'pending')}
                    className="flex items-center gap-1 rounded border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50"
                  >
                    <ArrowPathIcon className="h-4 w-4" />
                    Annuler
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      )}
    </div>
  );
};

export default NameMatchStep;
